import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, User, Building2, MapPin, FileText, Send } from 'lucide-react';
import { contractService, erpDataService } from '../_shared/api';
import {
  CITIES,
  CONTRACT_FIXED_PAYLOAD,
  buildServicoDesc,
  calculateMonthly,
  generateLogin16,
} from '../_shared/utils/contracts';
import {
  brToIsoDate,
  isValidDateBR,
  maskCep,
  maskCnpj,
  maskCpf,
  maskDateBR,
  maskPhone,
} from '../_shared/utils/date';
import type {
  Contract,
  ContractType,
  DueDate,
  Plan,
  Pop,
  Vendedor,
} from '../_shared/types';
import Picker, { type PickerOption } from './Picker';
import DocumentsSection, { type PendingPhoto } from './DocumentsSection';
import { useModalKeys } from '../../../hooks/useModalKeys';

/**
 * ContractFormModal — formulário de novo contrato (PF/PJ) ou edição.
 * Porte do apps/prospect-web/src/components/ContractFormModal.tsx.
 *
 * Adaptações:
 *  - Imports usando paths relativos do _shared
 *  - Tema dark slate alinhado ao shell SAAS (slate-900 / slate-800)
 */

interface Props {
  type: ContractType;
  onClose: () => void;
  onSuccess: () => void;
  /** Se fornecido, o form abre em modo edição com dados pré-preenchidos */
  editingContract?: Contract;
}

interface PFForm {
  nome: string;
  cpfcnpj: string;
  rg: string;
  rg_emissor: string;
  sexo: 'M' | 'F' | '';
  estadocivil: 'S' | 'C' | 'D' | 'V' | '';
  nomemae: string;
  nomepai: string;
  profissao: string;
  nacionalidade: string;
  naturalidade: string;
  email: string;
  celular: string;
  datanasc: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  cep: string;
  pontoreferencia: string;
  observacao: string;
  vencimento_id: number | '';
  plano_id: number | '';
  vendedor_id: number | '';
  pop_id: number | '';
  pa_internet: number;
  pa_tv: number;
}

interface PJForm {
  nome: string;
  cpfcnpj: string;
  nomefantasia: string;
  datanasc: string;
  respempresa: string;
  respcpf: string;
  email: string;
  celular: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  cep: string;
  pontoreferencia: string;
  observacao: string;
  vencimento_id: number | '';
  plano_id: number | '';
  vendedor_id: number | '';
  pop_id: number | '';
  pa_internet: number;
  pa_tv: number;
}

type FormState = PFForm | PJForm;

const EMPTY_PF: PFForm = {
  nome: '', cpfcnpj: '', rg: '', rg_emissor: '',
  sexo: '', estadocivil: '',
  nomemae: '', nomepai: '', profissao: '',
  nacionalidade: 'Brasileira', naturalidade: '',
  email: '', celular: '', datanasc: '',
  logradouro: '', numero: '', complemento: '',
  bairro: '', cidade: '', cep: '', pontoreferencia: '',
  observacao: '',
  vencimento_id: '', plano_id: '', vendedor_id: '', pop_id: '',
  pa_internet: 0, pa_tv: 0,
};

const EMPTY_PJ: PJForm = {
  nome: '', cpfcnpj: '', nomefantasia: '', datanasc: '',
  respempresa: '', respcpf: '',
  email: '', celular: '',
  logradouro: '', numero: '', complemento: '',
  bairro: '', cidade: '', cep: '', pontoreferencia: '',
  observacao: '',
  vencimento_id: '', plano_id: '', vendedor_id: '', pop_id: '',
  pa_internet: 0, pa_tv: 0,
};

function isoDateToBR(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function num(v: unknown): number | '' {
  return v == null || v === '' ? '' : Number(v);
}

function dataToPF(data: Record<string, unknown> | undefined): PFForm {
  const sexoRaw = data?.sexo;
  const ecRaw = data?.estadocivil;
  return {
    nome: String(data?.nome ?? ''),
    cpfcnpj: String(data?.cpfcnpj ?? ''),
    rg: String(data?.rg ?? ''),
    rg_emissor: String(data?.rg_emissor ?? ''),
    sexo: sexoRaw === 'M' || sexoRaw === 'F' ? sexoRaw : '',
    estadocivil:
      ecRaw === 'S' || ecRaw === 'C' || ecRaw === 'D' || ecRaw === 'V' ? ecRaw : '',
    nomemae: String(data?.nomemae ?? ''),
    nomepai: String(data?.nomepai ?? ''),
    profissao: String(data?.profissao ?? ''),
    nacionalidade: String(data?.nacionalidade ?? 'Brasileira'),
    naturalidade: String(data?.naturalidade ?? ''),
    email: String(data?.email ?? ''),
    celular: String(data?.celular ?? ''),
    datanasc: isoDateToBR(String(data?.datanasc ?? '')),
    logradouro: String(data?.logradouro ?? ''),
    numero: String(data?.numero ?? ''),
    complemento: String(data?.complemento ?? ''),
    bairro: String(data?.bairro ?? ''),
    cidade: String(data?.cidade ?? ''),
    cep: String(data?.cep ?? ''),
    pontoreferencia: String(data?.pontoreferencia ?? ''),
    observacao: String(data?.observacao ?? ''),
    vencimento_id: num(data?.vencimento_id),
    plano_id: num(data?.plano_id),
    vendedor_id: num(data?.vendedor_id),
    pop_id: num(data?.pop_id),
    pa_internet: Number(data?.pa_internet) || 0,
    pa_tv: Number(data?.pa_tv) || 0,
  };
}

function dataToPJ(data: Record<string, unknown> | undefined): PJForm {
  return {
    nome: String(data?.nome ?? ''),
    cpfcnpj: String(data?.cpfcnpj ?? ''),
    nomefantasia: String(data?.nomefantasia ?? ''),
    datanasc: isoDateToBR(String(data?.datanasc ?? '')),
    respempresa: String(data?.respempresa ?? ''),
    respcpf: String(data?.respcpf ?? ''),
    email: String(data?.email ?? ''),
    celular: String(data?.celular ?? ''),
    logradouro: String(data?.logradouro ?? ''),
    numero: String(data?.numero ?? ''),
    complemento: String(data?.complemento ?? ''),
    bairro: String(data?.bairro ?? ''),
    cidade: String(data?.cidade ?? ''),
    cep: String(data?.cep ?? ''),
    pontoreferencia: String(data?.pontoreferencia ?? ''),
    observacao: String(data?.observacao ?? ''),
    vencimento_id: num(data?.vencimento_id),
    plano_id: num(data?.plano_id),
    vendedor_id: num(data?.vendedor_id),
    pop_id: num(data?.pop_id),
    pa_internet: Number(data?.pa_internet) || 0,
    pa_tv: Number(data?.pa_tv) || 0,
  };
}

export default function ContractFormModal({
  type,
  onClose,
  onSuccess,
  editingContract,
}: Props) {
  const isPF = type === 'F';
  const isEditing = !!editingContract;
  const [form, setForm] = useState<FormState>(() => {
    if (editingContract) {
      return isPF ? dataToPF(editingContract.data) : dataToPJ(editingContract.data);
    }
    return isPF ? EMPTY_PF : EMPTY_PJ;
  });
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  // Esc fecha (só quando nenhum Picker está aberto — pilha do hook);
  // sem Enter-confirma: formulário longo, envio é ação deliberada
  useModalKeys(onClose);
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState<'idle' | 'saving' | 'uploading'>('idle');
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const [plans, setPlans] = useState<Plan[]>([]);
  const [dueDates, setDueDates] = useState<DueDate[]>([]);
  const [pops, setPops] = useState<Pop[]>([]);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [picker, setPicker] = useState<null | 'plano' | 'vendedor'>(null);

  useEffect(() => {
    Promise.all([
      erpDataService.getPlans(),
      erpDataService.getDueDates(),
      erpDataService.getPops(),
      erpDataService.getVendedores(),
    ])
      .then(([p, d, po, v]) => {
        setPlans(p);
        setDueDates(d);
        setPops(po);
        setVendedores(v);
      })
      .catch(() => undefined);
  }, []);

  // Helper genérico de set — tipo loose mas safe na pratica
  function setField(key: string, value: unknown): void {
    setForm((f) => ({ ...f, [key]: value }) as FormState);
  }

  const selectedPlan = useMemo(
    () => plans.find((p) => p.erpId === form.plano_id),
    [plans, form.plano_id],
  );
  const selectedVendedor = useMemo(
    () => vendedores.find((v) => v.erpId === form.vendedor_id),
    [vendedores, form.vendedor_id],
  );
  const mensalidadeTotal = useMemo(() => {
    if (!selectedPlan) return 0;
    return calculateMonthly(selectedPlan.valor, form.pa_internet, form.pa_tv);
  }, [selectedPlan, form.pa_internet, form.pa_tv]);

  function validate(): string | null {
    const f = form;
    if (!f.nome.trim()) return isPF ? 'Nome é obrigatório' : 'Razão social é obrigatória';
    if (!f.cpfcnpj.trim()) return isPF ? 'CPF é obrigatório' : 'CNPJ é obrigatório';
    if (isPF) {
      const pf = f as PFForm;
      if (!pf.sexo) return 'Sexo é obrigatório';
      if (!pf.estadocivil) return 'Estado civil é obrigatório';
    } else {
      const pj = f as PJForm;
      if (!pj.respempresa.trim()) return 'Responsável é obrigatório';
      if (!pj.respcpf.trim()) return 'CPF do responsável é obrigatório';
    }
    if (!f.email.trim()) return 'E-mail é obrigatório';
    if (!f.celular.trim()) return 'Celular é obrigatório';
    if (!f.datanasc.trim())
      return isPF ? 'Data de nascimento é obrigatória' : 'Data de fundação é obrigatória';
    if (!isValidDateBR(f.datanasc)) return 'Data inválida (use DD/MM/AAAA)';
    if (!f.logradouro.trim()) return 'Logradouro é obrigatório';
    if (!f.numero.trim()) return 'Número é obrigatório';
    if (!f.bairro.trim()) return 'Bairro é obrigatório';
    if (!f.cidade) return 'Cidade é obrigatória';
    if (!f.cep.trim()) return 'CEP é obrigatório';
    if (!f.vencimento_id) return 'Vencimento é obrigatório';
    if (!f.plano_id) return 'Plano é obrigatório';
    if (!f.vendedor_id) return 'Vendedor é obrigatório';
    if (!f.pop_id) return 'POP é obrigatório';
    return null;
  }

  async function handleSubmit(): Promise<void> {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSubmitting(true);
    try {
      const planDesc = selectedPlan?.descricao ?? '';
      const servicodesc = buildServicoDesc(planDesc, form.pa_internet, form.pa_tv);

      const basePayload: Record<string, unknown> = {
        nome: form.nome.toUpperCase(),
        cpfcnpj: form.cpfcnpj.replace(/\D/g, ''),
        email: form.email,
        celular: form.celular.replace(/\D/g, ''),
        datanasc: brToIsoDate(form.datanasc),
        logradouro: form.logradouro.toUpperCase(),
        numero: form.numero,
        complemento: form.complemento.toUpperCase(),
        bairro: form.bairro.toUpperCase(),
        cidade: form.cidade,
        cep: form.cep.replace(/\D/g, ''),
        pontoreferencia: form.pontoreferencia.toUpperCase(),
        vencimento_id: form.vencimento_id,
        plano_id: form.plano_id,
        vendedor_id: form.vendedor_id,
        pop_id: form.pop_id,
        observacao: form.observacao.toUpperCase(),
        servicodesc,
        pa_internet: form.pa_internet,
        pa_tv: form.pa_tv,
        login: generateLogin16(),
        ...CONTRACT_FIXED_PAYLOAD,
      };

      let payload: Record<string, unknown>;
      if (isPF) {
        const pf = form as PFForm;
        payload = {
          ...basePayload,
          rg: pf.rg,
          rg_emissor: pf.rg_emissor,
          sexo: pf.sexo,
          estadocivil: pf.estadocivil,
          nomemae: pf.nomemae.toUpperCase(),
          nomepai: pf.nomepai.toUpperCase(),
          profissao: pf.profissao.toUpperCase(),
          nacionalidade: pf.nacionalidade,
          naturalidade: pf.naturalidade.toUpperCase(),
        };
      } else {
        const pj = form as PJForm;
        payload = {
          ...basePayload,
          nomefantasia: pj.nomefantasia.toUpperCase(),
          respempresa: pj.respempresa.toUpperCase(),
          respcpf: pj.respcpf.replace(/\D/g, ''),
        };
      }

      setSubmitStep('saving');
      let contractId: string;
      if (isEditing && editingContract) {
        const { contract } = await contractService.update(editingContract.id, type, payload);
        contractId = contract.id;
      } else {
        const { contract } = await contractService.create(type, payload);
        contractId = contract.id;
      }

      if (photos.length > 0) {
        setSubmitStep('uploading');
        setUploadProgress({ current: 0, total: photos.length });
        for (let i = 0; i < photos.length; i++) {
          const p = photos[i];
          if (!p) continue;
          await contractService.uploadDocument(contractId, p.file, p.descricao || p.file.name);
          setUploadProgress({ current: i + 1, total: photos.length });
        }
      }

      alert(isEditing ? 'Contrato reenviado para análise!' : 'Contrato enviado para análise!');
      onSuccess();
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro: ' + (err.response?.data?.message ?? err.message ?? 'Falha ao enviar'));
    } finally {
      setSubmitting(false);
      setSubmitStep('idle');
    }
  }

  const planoOptions: PickerOption[] = plans.map((p) => ({
    value: p.erpId,
    label: `${p.erpId} - ${p.descricao}`,
    sublabel: `R$ ${p.valor.toFixed(2)}`,
  }));
  const vendedorOptions: PickerOption[] = vendedores.map((v) => ({
    value: v.erpId,
    label: v.nome,
  }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center sm:justify-center sm:p-4">
      <div className="bg-surface w-full sm:max-w-3xl sm:rounded-2xl border-t sm:border border-line flex flex-col max-h-[100vh] sm:max-h-[92vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isPF ? 'bg-primary-solid' : 'bg-info-solid'
              }`}
            >
              {isPF ? (
                <User size={20} className="text-white" />
              ) : (
                <Building2 size={20} className="text-white" />
              )}
            </div>
            <div>
              <h2 className="font-bold text-fg">
                {isEditing ? 'Editar contrato' : 'Novo contrato'}
              </h2>
              <p className="text-xs text-fg-2">
                {isPF ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={22} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          <Section icon={<User size={14} />} title={isPF ? 'Dados pessoais' : 'Empresa'}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={isPF ? 'Nome completo *' : 'Razão social *'}
                value={form.nome}
                onChange={(v) => setField('nome', v)}
                uppercase
                span2
              />
              <Field
                label={isPF ? 'CPF *' : 'CNPJ *'}
                value={form.cpfcnpj}
                onChange={(v) => setField('cpfcnpj', isPF ? maskCpf(v) : maskCnpj(v))}
                inputMode="numeric"
              />
              {!isPF && (
                <Field
                  label="Nome fantasia"
                  value={(form as PJForm).nomefantasia}
                  onChange={(v) => setField('nomefantasia', v)}
                  uppercase
                />
              )}
              {isPF && (
                <>
                  <Field
                    label="RG"
                    value={(form as PFForm).rg}
                    onChange={(v) => setField('rg', v)}
                  />
                  <Field
                    label="Emissor"
                    value={(form as PFForm).rg_emissor}
                    onChange={(v) => setField('rg_emissor', v)}
                    uppercase
                  />
                  <Select
                    label="Sexo *"
                    value={(form as PFForm).sexo}
                    onChange={(v) => setField('sexo', v)}
                    options={[
                      { value: '', label: '—' },
                      { value: 'M', label: 'Masculino' },
                      { value: 'F', label: 'Feminino' },
                    ]}
                  />
                  <Select
                    label="Estado civil *"
                    value={(form as PFForm).estadocivil}
                    onChange={(v) => setField('estadocivil', v)}
                    options={[
                      { value: '', label: '—' },
                      { value: 'S', label: 'Solteiro' },
                      { value: 'C', label: 'Casado' },
                      { value: 'D', label: 'Divorciado' },
                      { value: 'V', label: 'Viúvo' },
                    ]}
                  />
                  <Field
                    label="Nome da mãe"
                    value={(form as PFForm).nomemae}
                    onChange={(v) => setField('nomemae', v)}
                    uppercase
                    span2
                  />
                  <Field
                    label="Nome do pai"
                    value={(form as PFForm).nomepai}
                    onChange={(v) => setField('nomepai', v)}
                    uppercase
                    span2
                  />
                  <Field
                    label="Profissão"
                    value={(form as PFForm).profissao}
                    onChange={(v) => setField('profissao', v)}
                    uppercase
                  />
                  <Field
                    label="Nacionalidade"
                    value={(form as PFForm).nacionalidade}
                    onChange={(v) => setField('nacionalidade', v)}
                  />
                  <Field
                    label="Naturalidade"
                    value={(form as PFForm).naturalidade}
                    onChange={(v) => setField('naturalidade', v)}
                    uppercase
                  />
                </>
              )}
              <Field
                label="E-mail *"
                value={form.email}
                onChange={(v) => setField('email', v)}
                type="email"
              />
              <Field
                label="Celular *"
                value={form.celular}
                onChange={(v) => setField('celular', maskPhone(v))}
                inputMode="tel"
              />
              <Field
                label={
                  isPF
                    ? 'Data de nascimento * (DD/MM/AAAA)'
                    : 'Data de fundação * (DD/MM/AAAA)'
                }
                value={form.datanasc}
                onChange={(v) => setField('datanasc', maskDateBR(v))}
                placeholder="DD/MM/AAAA"
                inputMode="numeric"
              />
            </div>
          </Section>

          {!isPF && (
            <Section icon={<User size={14} />} title="Responsável">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Nome do responsável *"
                  value={(form as PJForm).respempresa}
                  onChange={(v) => setField('respempresa', v)}
                  uppercase
                  span2
                />
                <Field
                  label="CPF do responsável *"
                  value={(form as PJForm).respcpf}
                  onChange={(v) => setField('respcpf', maskCpf(v))}
                  inputMode="numeric"
                />
              </div>
            </Section>
          )}

          <Section icon={<MapPin size={14} />} title="Endereço">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Logradouro *"
                value={form.logradouro}
                onChange={(v) => setField('logradouro', v)}
                uppercase
                span2
              />
              <Field
                label="Número *"
                value={form.numero}
                onChange={(v) => setField('numero', v)}
                inputMode="numeric"
              />
              <Field
                label="Complemento"
                value={form.complemento}
                onChange={(v) => setField('complemento', v)}
                uppercase
              />
              <Field
                label="Bairro *"
                value={form.bairro}
                onChange={(v) => setField('bairro', v)}
                uppercase
              />
              <Select
                label="Cidade *"
                value={form.cidade}
                onChange={(v) => setField('cidade', v)}
                options={[
                  { value: '', label: '—' },
                  ...CITIES.map((c) => ({ value: c, label: c })),
                ]}
              />
              <Field
                label="CEP *"
                value={form.cep}
                onChange={(v) => setField('cep', maskCep(v))}
                inputMode="numeric"
              />
              <Field
                label="Ponto de referência"
                value={form.pontoreferencia}
                onChange={(v) => setField('pontoreferencia', v)}
                uppercase
                span2
              />
            </div>
            <p className="text-xs text-muted mt-2">
              UF: SP · País: BR · Geolocalização obtida pelo endereço
            </p>
          </Section>

          <Section icon={<FileText size={14} />} title="Contrato">
            <div className="space-y-3">
              <PickerField
                label="Plano *"
                value={selectedPlan ? `${selectedPlan.erpId} - ${selectedPlan.descricao}` : ''}
                onClick={() => setPicker('plano')}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="P.A Internet"
                  value={String(form.pa_internet)}
                  onChange={(v) => setField('pa_internet', Number(v))}
                  options={[
                    { value: '0', label: 'Nenhum' },
                    { value: '1', label: '1x' },
                    { value: '2', label: '2x' },
                    { value: '3', label: '3x' },
                  ]}
                />
                <Select
                  label="P.A TV"
                  value={String(form.pa_tv)}
                  onChange={(v) => setField('pa_tv', Number(v))}
                  options={[
                    { value: '0', label: 'Nenhum' },
                    { value: '1', label: '1x' },
                    { value: '2', label: '2x' },
                    { value: '3', label: '3x' },
                  ]}
                />
              </div>

              {selectedPlan && (
                <div className="bg-primary-soft/20 border border-primary-line/50 rounded-xl p-3 flex items-center justify-between">
                  <span className="text-xs text-fg-2">Mensalidade total</span>
                  <span className="text-fg font-bold text-lg">
                    R$ {mensalidadeTotal.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Vencimento *"
                  value={String(form.vencimento_id)}
                  onChange={(v) => setField('vencimento_id', v ? Number(v) : '')}
                  options={[
                    { value: '', label: '—' },
                    ...dueDates.map((d) => ({ value: String(d.erpId), label: `Dia ${d.dia}` })),
                  ]}
                />
                <Select
                  label="POP *"
                  value={String(form.pop_id)}
                  onChange={(v) => setField('pop_id', v ? Number(v) : '')}
                  options={[
                    { value: '', label: '—' },
                    ...pops.map((p) => ({ value: String(p.erpId), label: p.pop })),
                  ]}
                />
              </div>

              <PickerField
                label="Vendedor *"
                value={selectedVendedor?.nome ?? ''}
                onClick={() => setPicker('vendedor')}
              />

              <Field
                label="Observação"
                value={form.observacao}
                onChange={(v) => setField('observacao', v)}
                uppercase
                multiline
              />
            </div>
          </Section>

          <Section icon={<FileText size={14} />} title="Documentos">
            <DocumentsSection pending={photos} onChange={setPhotos} />
          </Section>
        </div>

        {/* Footer */}
        <div className="border-t border-line px-5 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="h-11 px-4 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting}
            className="h-11 px-5 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">
                  {submitStep === 'uploading'
                    ? `Fotos ${uploadProgress.current}/${uploadProgress.total}…`
                    : 'Salvando…'}
                </span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>{isEditing ? 'Reenviar para análise' : 'Enviar para análise'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <Picker
        open={picker === 'plano'}
        title="Selecione o plano"
        options={planoOptions}
        selected={form.plano_id || undefined}
        searchable
        onSelect={(o) => setField('plano_id', Number(o.value))}
        onClose={() => setPicker(null)}
      />
      <Picker
        open={picker === 'vendedor'}
        title="Selecione o vendedor"
        options={vendedorOptions}
        selected={form.vendedor_id || undefined}
        searchable
        onSelect={(o) => setField('vendedor_id', Number(o.value))}
        onClose={() => setPicker(null)}
      />
    </div>
  );
}

// ===== Layout helpers =====

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-primary">{icon}</span>
        <h3 className="text-xs font-bold uppercase tracking-wider text-primary">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type,
  inputMode,
  uppercase,
  multiline,
  span2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  uppercase?: boolean;
  multiline?: boolean;
  span2?: boolean;
}) {
  return (
    <label className={`block ${span2 ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] uppercase tracking-wide text-fg-2">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) =>
            onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)
          }
          placeholder={placeholder}
          rows={3}
          className="mt-1 w-full bg-canvas border border-line rounded-xl px-3 py-2 text-fg text-sm outline-none focus:border-primary placeholder:text-faint"
        />
      ) : (
        <input
          value={value}
          onChange={(e) =>
            onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)
          }
          placeholder={placeholder}
          type={type ?? 'text'}
          inputMode={inputMode}
          autoCapitalize={uppercase ? 'characters' : 'sentences'}
          className="mt-1 w-full bg-canvas border border-line rounded-xl px-3 h-11 text-fg text-sm outline-none focus:border-primary placeholder:text-faint"
        />
      )}
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-fg-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-canvas border border-line rounded-xl px-3 h-11 text-fg text-sm outline-none focus:border-primary"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function PickerField({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="w-full text-left">
      <span className="text-[11px] uppercase tracking-wide text-fg-2">{label}</span>
      <div className="mt-1 w-full bg-canvas border border-line rounded-xl px-3 h-11 flex items-center justify-between text-fg text-sm hover:border-primary">
        <span className={value ? '' : 'text-faint'}>{value || 'Selecione...'}</span>
        <span className="text-muted">›</span>
      </div>
    </button>
  );
}
